import { useId, useState } from 'react'
import type { FiltrosDeChamado } from '../../../api/filtrosDeChamado'
import { Botao } from '../../../components/Botao'
import { IconeFiltro, IconeSeta } from '../../../components/Icones'
import { cn } from '../../../lib/cn'
import { TEXTOS } from '../../../lib/glossario'
import { CamposDeFiltro } from './CamposDeFiltro'
import { quantidadeDeFiltros } from './filtros'

interface Props {
  filtros: FiltrosDeChamado
  alterar: (mudanca: Partial<FiltrosDeChamado>) => void
  limpar: () => void
  temFiltro: boolean
}

/**
 * O painel de filtros, recolhido por padrão.
 *
 * Recolhido porque a maior parte das triagens é um clique nos atalhos acima, e um bloco com seis
 * campos e três grupos de caixas sempre aberto empurraria a primeira linha da tabela para fora da
 * tela — a pessoa entraria na lista e veria filtro, não chamado.
 *
 * O contador no botão é o que torna o recolhimento honesto. Com o painel fechado, um recorte
 * herdado de um link fica invisível, e lista recortada que parece lista inteira é como se conta
 * errado — que neste módulo significa contar chamados a menos num mês de fiscalização.
 *
 * Fechado, o conteúdo não fica escondido por CSS: ele não existe no DOM. Painel oculto com
 * `display:none` continua tendo campos que o Tab alcança em alguns navegadores, e aí o foco some
 * dentro de uma caixa que ninguém vê.
 *
 * @param props critérios correntes e as funções que os alteram
 * @returns o painel de filtros
 */
export function FiltrosDaLista({ filtros, alterar, limpar, temFiltro }: Props) {
  const [aberto, setAberto] = useState(false)
  const idDoPainel = useId()
  const quantidade = quantidadeDeFiltros(filtros)

  return (
    <section aria-label="Filtros" className="cartao">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setAberto((valor) => !valor)}
          aria-expanded={aberto}
          aria-controls={idDoPainel}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg px-2 py-1 text-sm font-medium text-slate-800',
            'transition-colors hover:bg-slate-100 focus-visible:outline focus-visible:outline-2',
            'focus-visible:outline-offset-2 focus-visible:outline-niteroi-600',
          )}
        >
          <IconeFiltro className="h-4 w-4 text-slate-500" />
          Filtros
          {quantidade > 0 && (
            <span className="rounded-full bg-niteroi-50 px-2 py-0.5 text-xs font-semibold text-niteroi-800">
              {quantidade}
            </span>
          )}
          <IconeSeta className={cn('h-4 w-4 text-slate-400 transition-transform', aberto && 'rotate-90')} />
        </button>

        {/* Sempre visível, e desabilitado quando não há o que limpar: aparecer e sumir conforme o
            filtro faria os botões ao lado dançarem a cada caixa marcada. */}
        <Botao tamanho="pequeno" onClick={limpar} disabled={!temFiltro}>
          {TEXTOS.limparFiltros}
        </Botao>
      </div>

      {aberto && (
        <div id={idDoPainel} className="border-t border-slate-100 px-4 py-4">
          <CamposDeFiltro filtros={filtros} alterar={alterar} />
        </div>
      )}
    </section>
  )
}
